import React, { useState, useEffect, useCallback } from 'react';
import { getSalesHeaders as getHeaders } from '../../utils/salesAuth';
import { useIsMobile } from '../../hooks/useIsMobile';
import { timeAgo, fmtDate as formatDate } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

function formatCurrency(val) { return val ? `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 0 })}` : '$0'; }

const STATUS_CONFIG = {
  draft: { bg: '#f1f5f9', color: '#475569', label: 'Draft' },
  sent: { bg: '#dbeafe', color: '#2563eb', label: 'Sent' },
  signed: { bg: '#dcfce7', color: '#16a34a', label: 'Signed' },
  paid: { bg: '#d1fae5', color: '#059669', label: 'Paid' },
  cancelled: { bg: '#fef2f2', color: '#dc2626', label: 'Cancelled' },
};

export default function InvoicesView() {
  const [tab, setTab] = useState('all');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales/invoices?status=${tab}`, { credentials: 'include', headers: getHeaders() });
      if (res.ok) setInvoices(await res.json());
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const updateStatus = async (id, status) => {
    const res = await fetch(`${API}/api/sales/invoices/${id}/status`, {
      method: 'PATCH', credentials: 'include', headers: getHeaders(),
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, status } : inv));
      if (selected?.id === id) setSelected(prev => ({ ...prev, status }));
    }
  };

  const downloadPdf = (estimateId, quoteNumber) => {
    window.open(`${API}/api/sales/estimates/${estimateId}/pdf`, '_blank');
  };

  const filtered = invoices.filter(inv => {
    if (!search) return true;
    const s = search.toLowerCase();
    const custName = (inv.customer?.name || '').toLowerCase();
    const custEmail = (inv.customer?.email || '').toLowerCase();
    const qn = (inv.quote_number || '').toLowerCase();
    const title = (inv.project_title || '').toLowerCase();
    return custName.includes(s) || custEmail.includes(s) || qn.includes(s) || title.includes(s);
  });

  // Stats
  const totalValue = invoices.reduce((sum, i) => sum + (i.quoted_price || 0), 0);
  const signedValue = invoices.filter(i => i.status === 'signed' || i.status === 'paid').reduce((sum, i) => sum + (i.quoted_price || 0), 0);
  const pendingDeposits = invoices.filter(i => i.status === 'signed' && i.deposit_status !== 'received').length;

  return (
    <div data-testid="invoices-view" style={{ padding: isMobile ? '16px 12px' : '24px 28px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {selected ? (
        <InvoiceDetail invoice={selected} onBack={() => setSelected(null)} onStatusChange={updateStatus} onDownload={downloadPdf} isMobile={isMobile} />
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>Invoices & Quotes</h1>
          </div>

          {/* Stats Row */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 8 : 12, marginBottom: 16 }}>
            {[
              { label: 'Total Quotes', value: invoices.length, color: '#1e293b' },
              { label: 'Total Value', value: formatCurrency(totalValue), color: '#2563eb' },
              { label: 'Signed Value', value: formatCurrency(signedValue), color: '#16a34a' },
              { label: 'Pending Deposits', value: pendingDeposits, color: '#f59e0b' },
            ].map(s => (
              <div key={s.label} style={{ background: '#fff', borderRadius: 8, padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>{s.label}</div>
                <div style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Tabs + Search */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: 14, gap: 8, flexDirection: isMobile ? 'column' : 'row' }}>
            <div style={{ display: 'flex', gap: 4, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {[{ key: 'all', label: 'All' }, { key: 'draft', label: 'Drafts' }, { key: 'sent', label: 'Sent' }, { key: 'signed', label: 'Signed' }, { key: 'paid', label: 'Paid' }].map(f => (
                <button key={f.key} data-testid={`inv-tab-${f.key}`} onClick={() => { setTab(f.key); setSelected(null); }}
                  style={{ padding: '6px 14px', borderRadius: 6, border: tab === f.key ? 'none' : '1px solid #d1d5db',
                    background: tab === f.key ? '#1e293b' : '#fff', color: tab === f.key ? '#fff' : '#64748b',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 36 }}>
                  {f.label}
                </button>
              ))}
            </div>
            <input data-testid="inv-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search quotes..." style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, width: isMobile ? '100%' : 200, outline: 'none', boxSizing: 'border-box' }} />
          </div>

          {/* List */}
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading invoices...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
              <p style={{ fontSize: 14, marginBottom: 8 }}>{search ? 'No matching invoices' : 'No invoices yet'}</p>
              <p style={{ fontSize: 12 }}>Quotes created in the AI Estimate Generator will appear here</p>
            </div>
          ) : isMobile ? (
            /* Mobile: Card layout */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map(inv => {
                const st = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
                const custName = inv.customer?.name || inv.customer_info?.name || 'No customer';
                return (
                  <div key={inv.id} data-testid={`inv-row-${inv.id}`} onClick={() => setSelected(inv)}
                    style={{ background: '#fff', borderRadius: 10, padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', cursor: 'pointer', borderLeft: `4px solid ${st.color}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#c0392b', fontFamily: 'monospace' }}>{inv.quote_number}</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{formatCurrency(inv.quoted_price)}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{custName}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.project_title}</div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: st.bg, color: st.color }}>{st.label}</span>
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>{formatDate(inv.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop: Table layout */
            <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
              <div style={{ overflowX: 'auto' }}>
              {/* Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 110px 90px 80px 70px 42px', gap: 8, padding: '8px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <span>Quote #</span><span>Customer / Project</span><span>Amount</span><span>Status</span><span>Deposit</span><span>Date</span><span></span>
              </div>
              {filtered.map(inv => {
                const st = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
                const custName = inv.customer?.name || inv.customer_info?.name || '';
                return (
                  <div key={inv.id} data-testid={`inv-row-${inv.id}`}
                    style={{ display: 'grid', gridTemplateColumns: '140px 1fr 110px 90px 80px 70px 42px', gap: 8, alignItems: 'center', padding: '10px 16px',
                      borderBottom: '1px solid #f1f5f9', transition: 'background .1s' }}
                    onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                    <span onClick={() => setSelected(inv)} style={{ fontSize: 12, fontWeight: 700, color: '#c0392b', fontFamily: 'monospace', cursor: 'pointer' }}>{inv.quote_number}</span>
                    <div onClick={() => setSelected(inv)} style={{ cursor: 'pointer', minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {custName || 'No customer'}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inv.project_title || inv.services?.map(s => s.replace(/_/g, ' ')).join(', ') || ''}
                      </div>
                    </div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{formatCurrency(inv.quoted_price)}</span>
                    <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: st.bg, color: st.color, textAlign: 'center' }}>{st.label}</span>
                    <span style={{ fontSize: 11, color: inv.deposit_status === 'received' ? '#059669' : '#f59e0b', fontWeight: 600 }}>
                      {inv.status === 'signed' || inv.status === 'paid' ? (inv.deposit_status === 'received' ? 'Received' : 'Pending') : '—'}
                    </span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{timeAgo(inv.created_at)}</span>
                    <button data-testid={`inv-dl-${inv.id}`} onClick={(e) => { e.stopPropagation(); downloadPdf(inv.id, inv.quote_number); }}
                      title="Download PDF"
                      style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fef2f2', border: 'none', borderRadius: 6, cursor: 'pointer', flexShrink: 0 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                    </button>
                  </div>
                );
              })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


/* ═══════════════ INVOICE DETAIL ═══════════════ */
function InvoiceDetail({ invoice: inv, onBack, onStatusChange, onDownload, isMobile }) {
  const st = STATUS_CONFIG[inv.status] || STATUS_CONFIG.draft;
  const custName = inv.customer?.name || inv.customer_info?.name || 'No customer';
  const custEmail = inv.customer?.email || inv.customer_info?.email || '';
  const custPhone = inv.customer?.phone || inv.customer_info?.phone || '';
  const schedule = inv.payment_schedule || {};
  const isExpired = inv.valid_through && new Date(inv.valid_through) < new Date();
  const daysLeft = inv.valid_through ? Math.ceil((new Date(inv.valid_through) - new Date()) / 86400000) : null;

  return (
    <div data-testid="invoice-detail">
      <button onClick={onBack} data-testid="inv-back-btn"
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        Back to invoices
      </button>

      {/* Header Card */}
      <div style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '14px 12px' : '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h2 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>{inv.quote_number}</h2>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.project_title || inv.services?.map(s => s.replace(/_/g, ' ')).join(', ')}</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            <span style={{ padding: '4px 10px', borderRadius: 5, fontSize: 11, fontWeight: 700, background: st.bg, color: st.color }}>{st.label}</span>
            {isExpired && <span style={{ padding: '4px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: '#fef2f2', color: '#dc2626' }}>EXPIRED</span>}
            {!isExpired && daysLeft !== null && daysLeft <= 7 && (
              <span style={{ padding: '4px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: daysLeft <= 3 ? '#fef2f2' : '#fef9c3', color: daysLeft <= 3 ? '#dc2626' : '#ca8a04' }}>
                {daysLeft}d left
              </span>
            )}
          </div>
        </div>

        {/* Customer + Amount Row */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: isMobile ? 12 : 16, fontSize: 13 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase' }}>Customer</div>
            <div style={{ fontWeight: 600, color: '#1e293b' }}>{custName}</div>
            {custEmail && <a href={`mailto:${custEmail}`} style={{ display: 'block', fontSize: 12, color: '#2563eb' }}>{custEmail}</a>}
            {custPhone && <a href={`tel:${custPhone}`} style={{ display: 'block', fontSize: 12, color: '#c0392b' }}>{custPhone}</a>}
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase' }}>Quoted Amount</div>
            <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#1e293b' }}>{formatCurrency(inv.quoted_price)}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>Created {formatDate(inv.created_at)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4, textTransform: 'uppercase' }}>Valid Through</div>
            <div style={{ fontWeight: 600, color: isExpired ? '#dc2626' : '#1e293b' }}>{formatDate(inv.valid_through)}</div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      {inv.line_items?.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 10, padding: isMobile ? '12px 10px' : '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', marginBottom: 14 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Line Items</h3>
          <div style={{ borderRadius: 6, border: '1px solid #e2e8f0', overflow: 'hidden', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px', gap: 8, padding: '8px 12px', background: '#f8fafc', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', minWidth: 400 }}>
              <span>Item</span><span>Qty</span><span>Unit Price</span><span>Total</span>
            </div>
            {inv.line_items.map((item, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 100px 100px', gap: 8, padding: '8px 12px', borderTop: '1px solid #f1f5f9', fontSize: 13 }}>
                <span style={{ fontWeight: 500, color: '#334155' }}>{item.description || item.service_name}</span>
                <span style={{ color: '#64748b' }}>{item.quantity} {item.unit || ''}</span>
                <span style={{ color: '#64748b' }}>{formatCurrency(item.unit_price)}</span>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{formatCurrency(item.total || (item.quantity * item.unit_price))}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Schedule */}
      {schedule.deposit_amount && (
        <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', marginBottom: 14 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Payment Schedule (50/30/20)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12 }}>
            {[
              { label: 'Deposit (50%)', amount: schedule.deposit_amount, terms: schedule.deposit_terms, status: inv.deposit_status === 'received' },
              { label: 'Progress (30%)', amount: schedule.progress_amount, terms: schedule.progress_terms },
              { label: 'Final (20%)', amount: schedule.final_amount, terms: schedule.final_terms },
            ].map(p => (
              <div key={p.label} style={{ padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 4 }}>{p.label}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>{formatCurrency(p.amount)}</div>
                {p.status !== undefined && (
                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 700,
                    background: p.status ? '#dcfce7' : '#fef9c3', color: p.status ? '#16a34a' : '#ca8a04' }}>
                    {p.status ? 'Received' : 'Pending'}
                  </span>
                )}
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{p.terms}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Signature */}
      {inv.signature && (
        <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', marginBottom: 14 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#16a34a', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            E-Signature
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, fontSize: 13 }}>
            <div>
              <div style={{ fontFamily: "'Dancing Script', cursive, serif", fontSize: 24, color: '#1e293b', marginBottom: 4 }}>{inv.signature.typed_name}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{inv.signature.title}</div>
            </div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              <div>Signed: {formatDate(inv.signature.signed_at)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button data-testid="inv-download-pdf" onClick={() => onDownload(inv.id, inv.quote_number)}
          style={{ padding: '8px 16px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, minHeight: 44 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
          Download PDF
        </button>
        {inv.status === 'draft' && (
          <button data-testid="inv-mark-sent" onClick={() => onStatusChange(inv.id, 'sent')}
            style={{ padding: '8px 16px', background: '#dbeafe', color: '#2563eb', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
            Mark as Sent
          </button>
        )}
        {inv.status === 'signed' && (
          <button data-testid="inv-mark-paid" onClick={() => onStatusChange(inv.id, 'paid')}
            style={{ padding: '8px 16px', background: '#dcfce7', color: '#059669', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
            Mark as Paid
          </button>
        )}
        {(inv.status === 'draft' || inv.status === 'sent') && (
          <button data-testid="inv-cancel" onClick={() => onStatusChange(inv.id, 'cancelled')}
            style={{ padding: '8px 16px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
            Cancel
          </button>
        )}
        {inv.signature_token && !inv.signature && (
          <button data-testid="inv-copy-sign-link" onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/quote/sign/${inv.signature_token}`);
          }}
            style={{ padding: '8px 16px', background: '#fef9c3', color: '#a16207', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
            Copy Sign Link
          </button>
        )}
      </div>
    </div>
  );
}
